// Shared supplier-facing deadline escalation scale (deadline-reminder brief §1).
//
// ONE scale, read by every surface that shows a deadline — list rows, brief
// detail, the sticky bar and the dashboard banner — so they can never disagree.
// Everything here is DERIVED from status + deadline, never stored (mirrors the
// overdue-modifier rule in the look-and-feel brief §7). Call it fresh on every
// render, and drive a periodic re-render on long-open pages (see useNow), so a
// page left open overnight never shows a stale figure.
//
//   More than 7 days   → days countdown, neutral
//   7 days – 48 hours  → days countdown, amber
//   Under 48 hours     → HOURS countdown (minutes under 1h), red, closing-soon,
//                        at-risk (drives the banner, red row edge, top sort)
//   Passed             → overdue; a response is no longer possible

export const AT_RISK_MS = 48 * 60 * 60 * 1000;

const LONDON = 'Europe/London';

// Format a deadline instant in Fusion's operating timezone with its
// abbreviation, so a supplier in another timezone sees the true closing time
// and its clock — e.g. "Fri 25 Jul, 09:00 BST" (brief §5). Never format a
// deadline in the browser's local zone.
export function formatDeadlineLondon(iso, fallback = '—') {
  if (!iso) return fallback;
  const d = iso instanceof Date ? iso : new Date(iso);
  if (Number.isNaN(d.getTime())) return fallback;
  try {
    const parts = new Intl.DateTimeFormat('en-GB', {
      timeZone: LONDON, weekday: 'short', day: '2-digit', month: 'short',
      hour: '2-digit', minute: '2-digit', hour12: false, timeZoneName: 'short',
    }).formatToParts(d);
    const get = (t) => parts.find(p => p.type === t)?.value || '';
    const wd = get('weekday'), day = get('day'), mon = get('month').replace(/^Sept$/, 'Sep');
    const hh = get('hour'), mm = get('minute'), tz = get('timeZoneName');
    return `${wd} ${day} ${mon}, ${hh}:${mm} ${tz}`;
  } catch {
    return fallback;
  }
}

// Relative phrase for the time remaining, count-driven. Under 48h it's hours;
// under 1h it's minutes; at/over 48h it's whole days.
function remainingPhrase(ms) {
  if (ms < AT_RISK_MS) {
    const hours = Math.floor(ms / 3_600_000);
    if (hours >= 1) return { label: `${hours} hour${hours === 1 ? '' : 's'}`, short: `${hours}h` };
    const mins = Math.max(1, Math.floor(ms / 60_000));
    return { label: `${mins} minute${mins === 1 ? '' : 's'}`, short: `${mins}m` };
  }
  const days = Math.round(ms / 86_400_000);
  return { label: `${days} day${days === 1 ? '' : 's'}`, short: `${days}d` };
}

// Urgency state for a deadline, or null when urgency doesn't apply (no
// deadline, or the brief is closed / already submitted / declined). `status`
// is the recipient status, `briefStatus` the brief's. Pass `now` to keep a
// batch of rows consistent within one render.
export function deadlineUrgency(iso, { status, briefStatus, now } = {}) {
  if (!iso) return null;
  if (briefStatus === 'closed') return null;
  if (status === 'declined' || status === 'responded') return null;
  const deadline = iso instanceof Date ? iso : new Date(iso);
  if (Number.isNaN(deadline.getTime())) return null;
  const nowMs = (now instanceof Date ? now.getTime() : now) || Date.now();
  const ms = deadline.getTime() - nowMs;

  if (ms < 0) {
    return { band: 'overdue', atRisk: false, closingSoon: true, tone: 'text-[#E0290A]',
      label: 'Deadline passed', short: 'Passed', msRemaining: ms, hoursRemaining: 0 };
  }
  const { label, short } = remainingPhrase(ms);
  const hoursRemaining = ms / 3_600_000;
  if (ms < AT_RISK_MS) {
    return { band: 'imminent', atRisk: true, closingSoon: true, tone: 'text-[#E0290A]',
      label: `Closes in ${label}`, short, msRemaining: ms, hoursRemaining };
  }
  const days = Math.round(ms / 86_400_000);
  if (days <= 7) {
    return { band: 'approaching', atRisk: false, closingSoon: false, tone: 'text-[#7A4E10]',
      label: `Closes in ${label}`, short, msRemaining: ms, hoursRemaining };
  }
  return { band: 'comfortable', atRisk: false, closingSoon: false, tone: 'text-[#51534A]',
    label: `Closes in ${label}`, short, msRemaining: ms, hoursRemaining };
}
